import { useSelector } from 'react-redux';
import { TMutationContext } from './types';
import { selectJWT } from '../../store/selector/selectJWT';
import { selectLocale } from '../../store/selector/selectLocale';
import { useLocation, useParams } from 'react-router-dom';
import { selectCustomStates } from '../../store/selector/selectStates';
import { RootState } from '../../store';
import { selectExpressions } from '../../store/selector/selectExpressions';
import { useMemo } from 'react';
import { selectMe } from '../../store/selector/selectMe';
import actions from '../../utils/actions';
import { selectGlobalConstants } from '../../store/selector/selectGlobalConstants';
import QueryString from 'qs';
import { api } from '../../config';
import * as config from '../../config';
import { constructContexts } from './constructContexts';
import { merge } from 'lodash';
import { constructExpressions } from './constructExpressions';
import { selectDictionaries } from '../../store/selector/selectDictionaries';

const EMPTY_OBJ = Object.freeze({});

const useContexts = (
  selectors: string[] = [],
  customContexts?: Partial<TMutationContext>
): Partial<TMutationContext> => {
  const meSelector = useSelector((state: RootState) =>
    selectors.includes('me') ? selectMe(state) : undefined
  );
  const me = useMemo(() => meSelector, [meSelector]);

  const jwtSelector = useSelector((state: RootState) =>
    selectors.includes('jwt') ? selectJWT(state) : undefined
  );
  const jwt = useMemo(() => jwtSelector, [jwtSelector]);

  const localeSelector = useSelector((state: RootState) =>
    selectors.includes('locale') ? selectLocale(state) : config.app.default.locale
  );
  const locale = useMemo(() => localeSelector, [localeSelector]);

  const statesSelector = useSelector((state: RootState) =>
    selectors.includes('states') ? selectCustomStates(state) : EMPTY_OBJ
  );
  const states = useMemo(() => statesSelector, [statesSelector]);

  const expressionsSelector: RootState['site']['expressions'] = useSelector((state: RootState) =>
    selectors.includes('expressions') ? selectExpressions(state) : EMPTY_OBJ
  );
  const expressions = useMemo(
    () => constructExpressions(expressionsSelector),
    [expressionsSelector]
  );

  const constantsSelector = useSelector((state: RootState) =>
    selectors.includes('constants') ? selectGlobalConstants(state) : EMPTY_OBJ
  );
  const constants = useMemo(() => constantsSelector, [constantsSelector]);

  const dictionariesSelector = useSelector((state: RootState) =>
    selectors.includes('dictionaries') ? selectDictionaries(state) : EMPTY_OBJ
  );
  const dictionaries = useMemo(() => constantsSelector, [dictionariesSelector]);

  const locationFull = useLocation();
  const paramsFull = useParams();
  const location = selectors.includes('location') ? locationFull : undefined;
  const params = selectors.includes('params') ? paramsFull : undefined;
  const serverHost = api.host;
  const host = document.location.host;

  const staticContext = constructContexts();

  const context = useMemo(
    () =>
      merge(
        staticContext,
        {
          me,
          jwt,
          locale,
          states,
          exp: expressions,
          fn: actions,
          document: {
            serverHost,
            host,
            pathname: location?.pathname,
            params,
            query: location?.search ? QueryString.parse(location.search.substring(1)) : undefined,
          },
          constants,
          dictionaries,
        } as Partial<TMutationContext>,
        customContexts
      ),
    [
      staticContext,
      me,
      jwt,
      locale,
      states,
      expressions,
      serverHost,
      host,
      location?.pathname,
      location?.search,
      customContexts,
    ]
  );

  return context;
};

export default useContexts;
